import * as React from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import Dropzone from 'dropzone';
import notify from '../../../../shared/feedback/notifications';
import { isTranslationOf, Translate } from '../../../../shared/LanguageService';
import Api from '../../../../shared/Api';
import { previewTemplate } from '../../../../components/common/Dropzone';
import ImageProcessingApi from './api/ImageProcessingApi';
import { getUserManager } from '../../../../shared/userManager';

interface IState {
    uploading: boolean;
    uploadDone: boolean;
    uploadProgress: number;
    totalBytes: number;
    progressObject: any[];
    addedImages: number;
    uploadedImages: number;
}

interface IProps {
    orderId: number;
    orderRowId: number;
    orderRowFileNames: any[];
    doNotWarnAboutNotExistingFilenames?: boolean;
    onUploadDone: () => void;
    normalImagesDropzone?: boolean;
}

export default class EditedImagesDropzone extends React.Component<IProps, IState> {
    private _editedImagesDropzone: Dropzone;

    constructor(props: IProps) {
        super(props);
        Dropzone.autoDiscover = false;

        this.state = {
            uploading: false,
            uploadDone: false,
            uploadProgress: 0,
            totalBytes: 0,
            progressObject: [],
            addedImages: 0,
            uploadedImages: 0
        };

        this.processQueue = this.processQueue.bind(this);
        this.updateTotalBytes = this.updateTotalBytes.bind(this);
    }

    componentDidMount() {
        let component = this;
        this._editedImagesDropzone = new Dropzone(
            `#${this.props.normalImagesDropzone ? 'normal' : 'edited'}_images_dropzone${
                this.props.orderRowId
            }`,
            {
                acceptedFiles: 'image/jpeg,image/png,image/gif',
                dictDefaultMessage: Translate('dropzone/title'),
                dictFallbackMessage: Translate('dropzone/fallback'),
                dictInvalidFileType: Translate('dropzone/invalid-filetype'),
                dictRemoveFile: Translate('dropzone/remove-image'),
                dictCancelUpload: Translate('dropzone/cancel-upload'),
                dictCancelUploadConfirmation: Translate('dropzone/cancel-upload-confirmation'),
                autoProcessQueue: false,
                addRemoveLinks: true,
                parallelUploads: 1,
                uploadMultiple: false,
                previewTemplate: previewTemplate,
                maxThumbnailFilesize: 100,
                createImageThumbnails: true,
                accept: function (file: DropzoneFile, done: (error?: string | Error) => void) {
                    let editedImageFileName = file.name
                        .toLowerCase()
                        .substr(0, file.name.lastIndexOf('.'));

                    let matchingFileNames = component.props.orderRowFileNames.filter((fileName) => {
                        let uneditedImageFileName = fileName.name
                            .toLowerCase()
                            .substr(0, fileName.name.lastIndexOf('.'));

                        return uneditedImageFileName === editedImageFileName;
                    });

                    const isFileExtensionValid = () => {
                        for (let i = 0; i < matchingFileNames.length; i++) {
                            let isValid: boolean = true;
                            let existingFileName = matchingFileNames[i].name.toLowerCase();

                            let extension = existingFileName.substring(
                                existingFileName.lastIndexOf('.') + 1
                            );
                            let editedImageFileNameExtension = file.name
                                .toLowerCase()
                                .substring(file.name.lastIndexOf('.') + 1);

                            if (extension === editedImageFileNameExtension) {
                                return (isValid = true);
                            }

                            if (editedImageFileNameExtension === 'jpeg' && extension === 'jpg') {
                                return (isValid = false);
                            }

                            if (editedImageFileNameExtension === 'jpg' && extension === 'jpeg') {
                                return (isValid = false);
                            }

                            return isValid;
                        }
                    };

                    if (matchingFileNames.length > 0) {
                        let accepted = isFileExtensionValid();

                        if (!accepted) {
                            notify.failure(
                                `${file.name} ${Translate(
                                    'dropzone/valid-file-unvalid-file-extension'
                                )}`
                            );
                            component.setState({
                                progressObject: null
                            } as IState);
                            this.removeFile(file);
                        }
                    }

                    if (
                        matchingFileNames.length < 1 &&
                        component.props.doNotWarnAboutNotExistingFilenames !== true
                    ) {
                        file.previewElement.setAttribute(
                            'title',
                            Translate(
                                'image-processing/edited-images/dropzone/does-not-exist-on-order/warning/title'
                            )
                        );
                        file.previewElement.classList.add('warning');
                        let icon = document.createElement('i');
                        icon.classList.add('material-icons');
                        icon.classList.add('warning-icon');
                        icon.innerText = 'warning';
                        file.previewElement.appendChild(icon);
                        notify.warning(
                            `${file.name} ${Translate(
                                'image-processing/notification/does-not-exist-on-order'
                            )}`
                        );
                    }

                    done();
                },
                init: function () {
                    let dropzone = this;

                    this.on('addedfile', (file: DropzoneFile) => {
                        if (
                            dropzone.options.acceptedFiles &&
                            !dropzone.options.acceptedFiles.includes(file.type)
                        ) {
                            notify.failure(`${file.name} ${Translate('dropzone/invalid-filetype')}`);
                            component.setState({
                                progressObject: null
                            } as IState);
                            this.removeFile(file);
                            return;
                        }

                        component.updateTotalBytes(file.size);
                    });

                    this.on(
                        'sending',
                        (file: DropzoneFile, xhr: XMLHttpRequest, data: FormData) => {
                            new Api().applyHeadersToXhr(xhr);
                        }
                    );

                    this.on('queuecomplete', () => {
                        if (component.state.uploading) {
                            component.setState({ uploading: false } as IState);
                            notify.success(
                                Translate('image-processing/notification/images-uploaded')
                            );
                        }

                        component._editedImagesDropzone.removeAllFiles();
                        component.setState({
                            uploadDone: true,
                            totalBytes: 0,
                            progressObject: [],
                            uploadProgress: 0
                        } as IState);
                        this.options.autoProcessQueue = false;
                    });

                    this.on('processing', () => {
                        this.options.autoProcessQueue = true;
                    });

                    this.on(
                        'uploadprogress',
                        (file: DropzoneFile, progress: number, bytesSent: number) => {
                            let progressObject = component.state.progressObject;
                            if (!progressObject[file.name]) {
                                progressObject[file.name] = {};
                            }

                            progressObject[file.name] = bytesSent;

                            let totalProgress = 0;
                            for (let element in progressObject) {
                                totalProgress += progressObject[element];
                            }

                            let progressPercentage = parseInt(
                                ((totalProgress / component.state.totalBytes) * 100).toFixed()
                            );
                            component.setState({
                                uploadProgress: Math.min(progressPercentage, 100),
                                progressObject: progressObject
                            } as IState);
                        }
                    );

                    this.on('removedfile', (file: DropzoneFile) => {
                        component.updateTotalBytes(file.size * -1);
                    });

                    this.on('error', (file: DropzoneFile, message: string | Error) => {
                        if (
                            message &&
                            ((typeof message === 'string' &&
                                (message as string).indexOf('Authorization has been denied') !==
                                    -1) ||
                                (message as Error).message.indexOf(
                                    'Authorization has been denied'
                                ) !== -1)
                        ) {
                            let userManager = getUserManager();

                            userManager.signinSilent().then(() => {});

                            this.removeFile(file);
                            component.setState({
                                addedImages: component.state.addedImages - 1
                            } as IState);
                            this.addFile(file);

                            return;
                        }

                        if (isTranslationOf(message, 'dropzone/invalid-filetype')) {
                            return;
                        } else {
                            notify.failure(
                                Translate('image-processing/edited/file-upload/failed/text', {
                                    filename: file.name
                                }),
                                Translate('image-processing/edited/file-upload/failed/title'),
                                {
                                    closeButton: true,
                                    timeOut: 0,
                                    extendedTimeOut: 0
                                }
                            );
                        }
                    });

                    this.on('addedfile', (file) => {
                        if (file) {
                            component.setState({
                                addedImages: component.state.addedImages + 1
                            } as IState);
                        }
                    });

                    this.on('success', (file: DropzoneFile, response: any) => {
                        if (component.props.normalImagesDropzone) {
                            response.orderId = component.props.orderId;

                            ImageProcessingApi.setNormalImageInProgress(
                                component.props.orderId,
                                component.props.orderRowId,
                                response
                            ).then(() => {
                                component.setState(
                                    {
                                        uploadedImages: ++component.state.uploadedImages
                                    } as IState,
                                    () => {
                                        if (
                                            component.state.uploadedImages ===
                                            component.state.addedImages
                                        ) {
                                            component.props.onUploadDone();
                                            component.setState({
                                                addedImages: 0,
                                                uploadedImages: 0
                                            } as IState);
                                        }
                                    }
                                );
                            });
                        } else {
                            ImageProcessingApi.setImageInProgress(
                                component.props.orderId,
                                component.props.orderRowId,
                                response
                            ).then(() => {
                                component.setState(
                                    {
                                        uploadedImages: ++component.state.uploadedImages
                                    } as IState,
                                    () => {
                                        if (
                                            component.state.uploadedImages ===
                                            component.state.addedImages
                                        ) {
                                            component.props.onUploadDone();
                                            component.setState({
                                                addedImages: 0,
                                                uploadedImages: 0
                                            } as IState);
                                        }
                                    }
                                );
                            });
                        }
                    });
                }
            }
        );
    }

    processQueue() {
        this.setState(
            {
                uploadDone: false,
                uploading: true,
                uploadProgress: 0,
                progressObject: []
            } as IState,
            () => {
                this._editedImagesDropzone.processQueue();
            }
        );
    }

    updateTotalBytes(bytes: number) {
        let totalBytes = this.state.totalBytes + bytes;
        this.setState({ totalBytes: totalBytes } as IState);
    }

    render() {
        const { apiUrl } = window.appConfig;
        const baseTargetUrl = `${apiUrl}/views/orders/${this.props.orderId}/imageprocessing/rows/${this.props.orderRowId}`;
        const target = this.props.normalImagesDropzone
            ? `${baseTargetUrl}/normal`
            : `${baseTargetUrl}/edited`;

        return (
            <div>
                {this.state.uploading && (
                    <div className="dropzone-loading editedimages">
                        <ProgressBar
                            className="dropzone-progress"
                            active={this.state.uploadDone}
                            now={this.state.uploadProgress}
                            label={`${this.state.uploadProgress}%`}
                            max={100}
                            bsStyle="success"
                        />
                    </div>
                )}
                <form
                    action={target}
                    id={`${this.props.normalImagesDropzone ? 'normal' : 'edited'}_images_dropzone${
                        this.props.orderRowId
                    }`}
                    className="dropzone"
                ></form>
                <Button
                    bsStyle="info"
                    onClick={this.processQueue}
                    disabled={this.state.uploading || this.state.totalBytes === 0}
                >
                    <i className="material-icons">cloud_upload</i>{' '}
                    {Translate('image-processing/edited/button-labels/upload-images')}
                </Button>
            </div>
        );
    }
}
